import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useWishlist } from '../context/WishlistContext';
import useAuth from './useAuth';
import axios from 'axios';
import SummaryApi from '../common';
import { authHeader } from '../helpers/auth-header';

// Time intervals in milliseconds
const WISHLIST_REMINDER_INTERVAL = 24 * 60 * 60 * 1000; // 24 hours (time between reminders)
const CART_REMINDER_INTERVAL = 6 * 60 * 60 * 1000; // 6 hours (time between reminders)

// Grace period - minimum time items must be in wishlist/cart before first reminder appears
const WISHLIST_GRACE_PERIOD = 2 * 60 * 60 * 1000; // 2 hours - give user time to order after adding to wishlist
const CART_GRACE_PERIOD = 1 * 60 * 60 * 1000; // 1 hour - give user time to order after adding to cart

const STORAGE_KEYS = {
  wishlistDismissed: 'wishlistReminderDismissed',
  cartDismissed: 'cartReminderDismissed',
};

/**
 * Custom hook to manage reminder popups for wishlist and cart
 * Checks if items have been ordered and shows reminders accordingly
 */
export const useReminderPopups = () => {
  const { isAuthenticated } = useAuth();
  const { wishlistItems } = useWishlist();
  const { cartItems } = useSelector((state) => state.cart);
  const [showWishlistPopup, setShowWishlistPopup] = useState(false);
  const [showCartPopup, setShowCartPopup] = useState(false);
  const [orderedProductIds, setOrderedProductIds] = useState(new Set());
  const [loading, setLoading] = useState(false);

  // Fetch ordered products to check what has been ordered
  const fetchOrderedProducts = useCallback(async () => {
    if (!isAuthenticated) {
      setOrderedProductIds(new Set());
      return;
    }

    try {
      setLoading(true);
      const { data } = await axios.get(SummaryApi.orderList.url, {
        headers: authHeader(),
      });

      const orders = data?.listOrders?.rows || [];
      const orderedIds = new Set();

      // Extract all product variant IDs from orders
      orders.forEach((order) => {
        order?.items?.forEach((item) => {
          if (item?.product?.variant?.id) {
            // Store both variant ID and product ID for matching
            orderedIds.add(`${item.product.id}-${item.product.variant.id}`);
            orderedIds.add(item.product.variant.id.toString());
          }
        });
      });

      setOrderedProductIds(orderedIds);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrderedProductIds(new Set());
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Check if wishlist items have been ordered and grace period has passed
  const hasUnorderedWishlistItems = useCallback(() => {
    if (wishlistItems.length === 0) return false;

    const now = Date.now();
    
    // Check if any wishlist item hasn't been ordered AND grace period has passed
    return wishlistItems.some((item) => {
      const key = `${item.productId}-${item.variantId}`;
      const isNotOrdered = !orderedProductIds.has(key) && !orderedProductIds.has(item.variantId.toString());
      
      if (!isNotOrdered) return false;
      
      // Check if grace period has passed since item was added
      if (item.addedAt) {
        const addedTime = new Date(item.addedAt).getTime();
        const timeSinceAdded = now - addedTime;
        return timeSinceAdded >= WISHLIST_GRACE_PERIOD;
      }
      
      // If no addedAt timestamp, assume it's old enough (for backward compatibility)
      return true;
    });
  }, [wishlistItems, orderedProductIds]);

  // Check if cart items have been ordered and grace period has passed
  const hasUnorderedCartItems = useCallback(() => {
    const validCartItems = cartItems.filter(item => item.id !== 0 && item.variantDetails);
    if (validCartItems.length === 0) return false;

    const now = Date.now();

    // Check if any cart item hasn't been ordered AND grace period has passed
    return validCartItems.some((item) => {
      const variantId = item.variantDetails?.id || item.variantId;
      const productId = item.productDetails?.id || item.productId;
      const key = `${productId}-${variantId}`;
      const isNotOrdered = !orderedProductIds.has(key) && !orderedProductIds.has(variantId?.toString());
      
      if (!isNotOrdered) return false;
      
      // Check if grace period has passed since item was added to cart
      // Cart items from backend should have createdAt timestamp
      const createdAt = item.createdAt || item.created_at || item.addedAt;
      if (createdAt) {
        const addedTime = new Date(createdAt).getTime();
        const timeSinceAdded = now - addedTime;
        return timeSinceAdded >= CART_GRACE_PERIOD;
      }
      
      // If no timestamp available, assume it's old enough (for backward compatibility)
      // But also check if this is a very recent addition by checking if it exists
      // We'll be conservative and assume it's old if no timestamp
      return true;
    });
  }, [cartItems, orderedProductIds]);

  // Check if popup should be shown based on dismissal time
  const shouldShowPopup = useCallback((storageKey, interval) => {
    const dismissedAt = localStorage.getItem(storageKey);
    if (!dismissedAt) return true;

    const dismissedTime = parseInt(dismissedAt, 10);
    const now = Date.now();
    const timeSinceDismissal = now - dismissedTime;

    return timeSinceDismissal >= interval;
  }, []);

  // Handle wishlist popup dismissal
  const dismissWishlistPopup = useCallback(() => {
    localStorage.setItem(STORAGE_KEYS.wishlistDismissed, Date.now().toString());
    setShowWishlistPopup(false);
  }, []);

  // Handle cart popup dismissal
  const dismissCartPopup = useCallback(() => {
    localStorage.setItem(STORAGE_KEYS.cartDismissed, Date.now().toString());
    setShowCartPopup(false);
  }, []);

  // Main effect to fetch ordered products
  useEffect(() => {
    if (!isAuthenticated) {
      setShowWishlistPopup(false);
      setShowCartPopup(false);
      return;
    }

    // Fetch ordered products first
    fetchOrderedProducts();
  }, [isAuthenticated, fetchOrderedProducts]);

  // Check and show popups after orders are loaded and when items change
  useEffect(() => {
    if (loading || !isAuthenticated) {
      setShowWishlistPopup(false);
      setShowCartPopup(false);
      return;
    }

    // Add a small delay before checking popups (prevents immediate popup on page load)
    const timer = setTimeout(() => {
      // Check cart popup first (higher priority)
      const shouldShowCart = hasUnorderedCartItems() && shouldShowPopup(STORAGE_KEYS.cartDismissed, CART_REMINDER_INTERVAL);
      
      if (shouldShowCart) {
        // Cart popup has higher priority
        setShowCartPopup(true);
        setShowWishlistPopup(false);
      } else {
        setShowCartPopup(false);
        
        // Check wishlist popup only if cart popup is not showing
        const shouldShowWishlist = hasUnorderedWishlistItems() && shouldShowPopup(STORAGE_KEYS.wishlistDismissed, WISHLIST_REMINDER_INTERVAL);
        setShowWishlistPopup(shouldShowWishlist);
      }
    }, 3000); // 3 second delay after items load (gives user time to interact)

    return () => clearTimeout(timer);
  }, [
    loading,
    isAuthenticated,
    hasUnorderedWishlistItems,
    hasUnorderedCartItems,
    shouldShowPopup,
  ]);

  return {
    showWishlistPopup,
    showCartPopup,
    dismissWishlistPopup,
    dismissCartPopup,
    loading,
  };
};

